/*
  assets/mad-estimate.js
  Pure maths for the quote funnel. No DOM, no fetch. Runs in the browser
  (window.MADEstimate) and in Node (module.exports) so the same code is
  unit-tested offline and used on the storefront.

  What lives here:
    - parsing inches ("36 1/2", "36.5", "36-1/2")
    - slab size from a product title ("48x96", "42.25x95.25", "36 x 80",
      '48"x96"', "36x1.75x80" = width, thickness, height), with the Width /
      Height product options as the fallback
    - the door gate and configuration from tags (case-insensitive)
    - door kind (antique vs handcrafted): a Newly Built or Handcrafted tag
      first, then an Antique tag, then the title words, product_type last
    - replacement glass "priced on your quote" (answer 8 of 2026-09-25; the
      dormant by-pane path of D13 only with its switch on), the freight
      range (D8) and the trade discount on doors (D6, switch off)
    - restoration tiers expanding into the Zap's restore_* keys
    - the derived jamb type the Zap prices from
    - the honest three-state fit readout
    - the estimate, whose service lines equal the Zap's line-item sum for
      the same _raw (tests/unit/estimate.test.mjs proves it against the
      synced Zap code)

  Prices are NOT in this file. They come from assets/mad-price-book.json
  (Node) or window.MAD_PRICE_BOOK (browser, written by tools/sync-price-book.mjs).
*/
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.MADEstimate = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ── numbers ─────────────────────────────────────────────────── */

  // "36 1/2" -> 36.5 ; "36.5" -> 36.5 ; "36-1/2" -> 36.5 ; "1/2" -> 0.5 ; "36in" -> 36
  function parseInches(input) {
    if (input === null || input === undefined) return null;
    let s = String(input).trim().toLowerCase();
    if (!s) return null;
    s = s.replace(/["”″]|inches|inch|in\b/g, ' ').replace(/[–—]/g, '-').trim();
    // unicode vulgar fractions
    const vulgar = { '½': ' 1/2', '¼': ' 1/4', '¾': ' 3/4', '⅛': ' 1/8', '⅜': ' 3/8', '⅝': ' 5/8', '⅞': ' 7/8' };
    s = s.replace(/[½¼¾⅛⅜⅝⅞]/g, (m) => vulgar[m]);
    s = s.replace(/\s+/g, ' ').trim();
    let m = s.match(/^(\d+)(?:\s+|-)(\d+)\/(\d+)$/); // mixed fraction
    if (m) {
      const den = parseInt(m[3], 10);
      if (!den) return null;
      return parseInt(m[1], 10) + parseInt(m[2], 10) / den;
    }
    m = s.match(/^(\d+)\/(\d+)$/); // plain fraction
    if (m) {
      const den = parseInt(m[2], 10);
      if (!den) return null;
      return parseInt(m[1], 10) / den;
    }
    m = s.match(/^(\d+(?:\.\d+)?)$/);
    if (m) return parseFloat(m[1]);
    return null;
  }

  // 36.5 -> "36 1/2" (nearest 1/16), 36 -> "36"
  function formatInches(n) {
    if (n === null || n === undefined || isNaN(n)) return '';
    const sign = n < 0 ? '-' : '';
    n = Math.abs(n);
    let whole = Math.floor(n);
    let sixteenths = Math.round((n - whole) * 16);
    if (sixteenths === 16) { whole += 1; sixteenths = 0; }
    if (sixteenths === 0) return sign + whole;
    let num = sixteenths, den = 16;
    while (num % 2 === 0) { num /= 2; den /= 2; }
    return sign + (whole ? whole + ' ' : '') + num + '/' + den;
  }

  function roundTo(n, step) { return Math.round(n / step) * step; }
  function round2(n) { return Math.round(n * 100) / 100; }

  function money(n, opts) {
    const o = opts || {};
    if (n === null || n === undefined || isNaN(n)) return '';
    const neg = n < 0;
    const abs = Math.abs(n);
    const hasCents = Math.abs(abs - Math.round(abs)) > 0.004 && !o.whole;
    const fixed = hasCents ? abs.toFixed(2) : String(Math.round(abs));
    const parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (neg ? '-' : '') + '$' + parts.join('.');
  }

  /* ── tags, configuration, kind ───────────────────────────────── */

  function normalizeTags(tags) {
    if (!tags) return [];
    const arr = Array.isArray(tags) ? tags : String(tags).split(',');
    return arr.map((t) => String(t).trim().toLowerCase()).filter(Boolean);
  }

  const CONFIG_TAGS = { single: 'single', pair: 'pair', 'tri-fold': 'triple', 'trifold': 'triple', 'bi-fold': 'quad', 'bifold': 'quad' };

  // 'single' | 'pair' | 'triple' | 'quad' | null (null: not a door, no wizard)
  function doorConfig(tags) {
    const t = normalizeTags(tags);
    // Pair beats Single when both are present (a few listings carry both).
    if (t.includes('pair')) return 'pair';
    if (t.includes('bi-fold') || t.includes('bifold')) return 'quad';
    if (t.includes('tri-fold') || t.includes('trifold')) return 'triple';
    if (t.includes('single')) return 'single';
    return null;
  }

  function isDoor(tags) { return doorConfig(tags) !== null; }

  const CONFIG_LABELS = { single: 'Single door', pair: 'Pair', triple: 'Tri-fold set', quad: 'Bi-fold set' };

  function doorFlags(tags) {
    const t = normalizeTags(tags);
    return {
      glass: t.includes('glass'),
      /* Seth 2026-10-12: an arched frame only for a true arch; a square door with arched glass or carving carries the
         "Square top" tag (22 doors checked by their photos the same day) and is framed and priced square */
      arched: (t.includes('arched') || t.includes('arch') || t.includes('gothic')) && !t.includes('square top'),
      iron: t.includes('iron'),
    };
  }

  // Door kind, 2026-09-25 rule (door finder design, task QUOTE 6): the tags are the truth,
  // product_type is wrong or blank on about 109 listings (L520 "Handcrafted 8 Panel Arched
  // Double Doors" is typed Antique Doors and was offered restoration). Order:
  //   1. a "Newly Built" or "Handcrafted" tag -> handcrafted (a new door is never offered restoration)
  //   2. an "Antique" tag -> antique
  //   3. the title: the word "handcrafted" or "newly built" -> handcrafted; "antique" -> antique
  //   4. product_type as a fallback only; blank or unknown -> antique
  // snippets/mad-quote-door-kind.liquid is the same rule in Liquid. The title words are matched the
  // same way in both, character for character (Liquid has no regex): lower case, every character in
  // KIND_SEPARATORS becomes a space, a space on each side, then two passes that turn a double space
  // into one; then " handcrafted " / " newly built " / " antique " are looked for.
  // tests/unit/door-kind-parity.test.mjs runs both on one table.
  const KIND_SEPARATORS = ['-', '(', ')', ',', '.', ':', '/', '"', '|', '!', '?', '&', '#', '+', ';', '_', '*', '[', ']', "'"];
  function kindTitle(title) {
    let t = String(title || '').toLowerCase();
    for (const ch of KIND_SEPARATORS) t = t.split(ch).join(' ');
    t = ' ' + t + ' ';
    return t.split('  ').join(' ').split('  ').join(' ');
  }
  function detectDoorKind(product) {
    const t = normalizeTags(product && product.tags);
    if (t.includes('newly built') || t.includes('handcrafted')) return 'handcrafted';
    if (t.includes('antique')) return 'antique';
    const title = kindTitle(product && product.title);
    if (title.indexOf(' handcrafted ') !== -1 || title.indexOf(' newly built ') !== -1) return 'handcrafted';
    if (title.indexOf(' antique ') !== -1) return 'antique';
    const type = String((product && (product.productType || product.product_type || product.type)) || '').trim().toLowerCase();
    if (type.indexOf('handcrafted') !== -1) return 'handcrafted';
    return 'antique';
  }

  /* ── slab size ───────────────────────────────────────────────── */

  // "48x96", "42.25x95.25", "36 x 80", '48"x96"' (an inch mark before the x), and the
  // three-part "36x1.75x80", which is width x thickness x height. Same pattern as
  // tests/lib/contract.mjs parseSlabReference.
  const SIZE_RE = /(\d+(?:\.\d+)?)\s*["”″]?\s*[xX×]\s*(\d+(?:\.\d+)?)(?:\s*["”″]?\s*[xX×]\s*(\d+(?:\.\d+)?))?(?!\d)/;

  // options: array of {name, values} (Shopify product JSON) or {width, height}
  function parseSlabSize(title, options) {
    const m = String(title || '').match(SIZE_RE);
    if (m) {
      const three = m[3] !== undefined;
      const w = parseFloat(m[1]), h = parseFloat(three ? m[3] : m[2]);
      if (w > 0 && h > 0 && w < 400 && h < 400) {
        const out = { width: w, height: h, source: 'title' };
        if (three) out.thickness = parseFloat(m[2]);
        return out;
      }
    }
    if (options) {
      let w = null, h = null;
      if (Array.isArray(options)) {
        for (const opt of options) {
          const name = String((opt && opt.name) || '').trim().toLowerCase(); // "Width " with a trailing space parses too
          const first = opt && opt.values && opt.values[0];
          if (name === 'width') w = parseInches(first);
          if (name === 'height') h = parseInches(first);
        }
      } else if (typeof options === 'object') {
        w = parseInches(options.width);
        h = parseInches(options.height);
      }
      if (w && h) return { width: w, height: h, source: 'options' };
    }
    return null;
  }

  function formatSlab(slab) {
    if (!slab) return '';
    return formatInches(slab.width) + ' x ' + formatInches(slab.height) + ' in';
  }

  /* ── the trusted size (Codex final review R04, 2026-09-26) ───────── */

  // The size every estimate entry point (the full form from the door page, a collection card or a /cart line)
  // measures against: the one the product page shows. snippets/mad-door-facts.liquid decides it (metafields first; a
  // title or option size only when it can be trusted) and snippets/mad-size-json.liquid puts it in the product object
  // as `size`. null when the page withholds it (size.check: three_numbers, options_disagree, frame_listing) or has
  // none: then fitReadout gets no slab, so it computes no fit result and no resize target (talk, no_slab_size: the
  // opening goes to review) and the screens say SIZE_TO_CONFIRM. An object without `size` (a list saved before this
  // change) gets the page's own hold-backs here, on its title, options and tags; parseSlabSize itself is unchanged.
  const SIZE_TO_CONFIRM = 'Size to be confirmed';
  const SIZE_TO_CONFIRM_TEXT = 'Size to be confirmed. We review your measurements with you before any work begins.';
  // "32.5" yes; "30 - 39" (a filter range), "36 1/2" and "" no: the page reads plain decimal options only
  function plainInches(v) { const s = String(v == null ? '' : v).trim(); return /^(\d+\.?\d*|\.\d+)$/.test(s) ? parseFloat(s) : 0; }
  // a frame or unit listing: the listed size may be the unit, not the door (tag Jambed or Unit, or the title word)
  function frameListing(title, tags) {
    const t = normalizeTags(tags);
    if (t.includes('jambed') || t.includes('unit')) return true;
    return String(title || '').toLowerCase().replace(/[-,()]/g, ' ').split(' ').some((w) => ['unit', 'units', 'frame', 'framed', 'jamb', 'jambed'].includes(w));
  }
  function trustedSlab(product) {
    const p = product || {};
    const s = p.size;
    if (s && typeof s === 'object') {
      if (s.check) return null;
      const w = Number(s.width), h = Number(s.height);
      if (!(w > 0 && h > 0 && w < 400 && h < 400)) return null;
      const out = { width: w, height: h, source: String(s.source || 'facts') };
      const t = Number(s.thickness);
      if (t > 0) out.thickness = t;
      return out;
    }
    const slab = parseSlabSize(p.title, p.options);
    if (!slab) return null;
    let ow = 0, oh = 0;
    (Array.isArray(p.options) ? p.options : []).forEach((o) => {
      const name = String((o && o.name) || '').trim().toLowerCase(), v = o && o.values ? o.values[0] : '';
      if (name === 'width') ow = plainInches(v);
      if (name === 'height') oh = plainInches(v);
    });
    if (slab.source === 'title') {
      if (slab.thickness !== undefined && !(slab.thickness >= 0.75 && slab.thickness <= 3)) return null; // E157 40x25x92.25
      if ((ow > 0 && Math.abs(slab.width - ow) >= 1) || (oh > 0 && Math.abs(slab.height - oh) >= 1)) return null;
    } else if (!(ow > 0 && oh > 0)) return null;
    if (frameListing(p.title, p.tags)) return null;
    return slab;
  }

  // Trailing shop code from the product title, e.g. "... - N222" or "... L500" -> "N222" / "L500".
  // Used in form copy; dialog titles keep the full product name.
  const DOOR_CODE_RE = /\b([A-Za-z]{1,3}\d{2,5})\s*$/;
  function doorCode(title) {
    const m = String(title || '').trim().match(DOOR_CODE_RE);
    return m ? m[1].toUpperCase() : '';
  }
  function doorLabel(title) {
    return doorCode(title) || String(title || 'This door').trim() || 'This door';
  }

  /* ── restoration tiers ───────────────────────────────────────── */

  const RESTORE_KEYS = [
    'restore_remove_hardware', 'restore_square_edges', 'restore_patch_mortise',
    'restore_fix_cracks', 'restore_light_sand', 'restore_heavy_sand', 'restore_full',
  ];

  function tierKeys(tier, book) {
    const tiers = (book && book.restoration_tiers) || {};
    const def = tiers[tier];
    return def && Array.isArray(def.keys) ? def.keys.slice() : [];
  }

  // Returns an object with EVERY restore_* key: '1' for the tier's items, '' otherwise,
  // so a re-save from a lower tier clears the higher tier's keys.
  function expandTier(tier, book) {
    const on = new Set(tierKeys(tier, book));
    const out = {};
    for (const k of RESTORE_KEYS) out[k] = on.has(k) ? '1' : '';
    return out;
  }

  function tierPrice(tier, book) {
    const prices = (book && book.prices) || {};
    return round2(tierKeys(tier, book).reduce((s, k) => s + (Number(prices[k]) || 0), 0));
  }

  // Infer the tier from a legacy _raw (checkbox era). Exact set match first, then best guess.
  function tierFromRaw(raw, book) {
    if (!raw) return 'none';
    if (raw.restore_tier) return raw.restore_tier;
    const on = RESTORE_KEYS.filter((k) => raw[k] === '1');
    if (!on.length) return 'none';
    if (on.includes('restore_full')) return 'full';
    const tiers = (book && book.restoration_tiers) || {};
    const onSet = on.slice().sort().join('|');
    for (const name of Object.keys(tiers)) {
      if (name.charAt(0) === '_') continue;
      const keys = (tiers[name].keys || []).slice().sort().join('|');
      if (keys === onSet) return name;
    }
    if (on.includes('restore_heavy_sand')) return 'tier2_heavy';
    if (on.includes('restore_fix_cracks') || on.includes('restore_light_sand')) return 'tier2_light';
    return 'tier1';
  }

  /* ── derived jamb type (the Zap prices pair and arched off this key) ── */

  function deriveJambType(config, arched, application) {
    if (application === 'bifold') return 'bifold';
    const pair = config === 'pair' || config === 'quad';
    if (arched) return pair ? 'arched_pair' : 'arched_single';
    return pair ? 'square_pair' : 'square_single';
  }

  /* ── fit readout ─────────────────────────────────────────────── */

  // slab: {width,height} | null ; opening: {width,height} | null ; openingType: 'rough'|'finished'
  // flags: {glass, arched, iron}. Returns one of four honest states.
  function fitReadout(args) {
    const a = args || {};
    const book = a.book || {};
    const fit = book.fit || {};
    const slab = a.slab, opening = a.opening;
    if (!opening || !opening.width || !opening.height) {
      return { state: 'unmeasured', reason: 'no_opening', suggestedTarget: null };
    }
    if (!slab || !slab.width || !slab.height) {
      return { state: 'talk', reason: 'no_slab_size', suggestedTarget: null };
    }
    const clearance = (a.openingType === 'finished' ? fit.finished_clearance : fit.rough_clearance) || { width: 0, height: 0 };
    const band = typeof fit.close_band === 'number' ? fit.close_band : 1;
    const idealW = opening.width - (clearance.width || 0);
    const idealH = opening.height - (clearance.height || 0);
    const dW = round2(idealW - slab.width); // + means the door must grow, - means trim
    const dH = round2(idealH - slab.height);
    const suggested = { width: roundTo(idealW, 0.125), height: roundTo(idealH, 0.125) };
    const flags = a.flags || {};
    const type = flags.iron ? 'iron' : flags.glass ? 'glass' : 'solid';

    if (Math.abs(dW) <= band && Math.abs(dH) <= band) {
      return { state: 'close', reason: 'within_band', deltaW: dW, deltaH: dH, suggestedTarget: null };
    }
    if (flags.iron) {
      return { state: 'talk', reason: 'iron', deltaW: dW, deltaH: dH, suggestedTarget: null };
    }
    /* Seth 2026-10-13: an arched door is trimmed or built up in height like a square door; its width is never changed,
       so a width outside the close band needs a closer look and a resize keeps the listed width */
    if (flags.arched && fit.arched_width_fixed !== false) {
      if (Math.abs(dW) > band) return { state: 'talk', reason: 'arched_width', deltaW: dW, deltaH: dH, suggestedTarget: null };
      suggested.width = slab.width;
    }
    const trim = (fit.trim_allowance || {})[type];
    const add = (fit.add_allowance || {})[type];
    const over = (d) => (d < -band && typeof trim === 'number' && -d > trim) || (d > band && typeof add === 'number' && d > add);
    if (over(dW) || over(dH)) {
      return { state: 'talk', reason: 'beyond_allowance', deltaW: dW, deltaH: dH, suggestedTarget: null };
    }
    return { state: 'resize', reason: dW < -band || dH < -band ? 'trim' : 'add', deltaW: dW, deltaH: dH, suggestedTarget: suggested };
  }

  /* Seth 2026-10-11: one place to call, at the end of the request, so no state here sends the customer to the phone.
     The Fit line property carries this sentence to the draft; the Zap reads fit_readout, never these words. */
  const FIT_TEXT = {
    close: 'Close match. We review your measurements with you before any work begins.',
    resize: 'We can resize this door for your opening. We review your measurements with you before any work begins.',
    talk: 'This opening needs a closer look. We review your measurements with you before any work begins.',
    unmeasured: 'Not measured yet. You can add the size later.',
  };

  /* ── estimate (equals the Zap's line-item sum for the same _raw) ─ */

  function prehangPrice(raw, book) {
    const P = (book && book.prices) || {};
    const isPairJamb = ['square_pair', 'arched_pair', 'bifold'].includes(raw.jamb_type);
    const isArched = ['arched_single', 'arched_pair'].includes(raw.jamb_type);
    const isCypress = raw.jamb_grade === 'stain_cypress';
    const isWideJamb = raw.wall_depth === '6_9_16';
    let p = isPairJamb ? P.prehang_pair_base : P.prehang_single_base;
    if (isCypress) p += P.prehang_cypress_add;
    if (isWideJamb) p += P.prehang_wide_jamb_add;
    if (isArched) p = Math.round(p * P.prehang_arched_multiplier * 100) / 100;
    const isExterior = raw.int_ext === 'exterior';
    const magnetic = (!isExterior && raw.latch_type === 'magnetic') ? (P.prehang_magnetic_latch || 0) : 0;
    const exterior = isExterior ? P.prehang_exterior_upcharge : 0;
    return Math.round((p + magnetic + exterior) * 100) / 100;
  }

  // The raw an "Install Ready, details on the call" door carries: the Zap's TBD path.
  function defaultInstallRawFor(config, flags, intExt, application, book) {
    const ir = (book && book.install_ready) || {};
    const exterior = intExt === 'exterior';
    return {
      jamb_needed: 'yes',
      jamb_type: deriveJambType(config, flags && flags.arched, application),
      wall_depth: 'not_sure',
      swing: 'not_sure',
      hinge_color: '',
      latch_type: exterior ? '' : 'magnetic',
      jamb_grade: exterior ? (ir.exterior_grade || 'stain_cypress') : 'not_sure',
      int_ext: intExt || 'interior',
    };
  }

  // "Install Ready from $X" for the product page: square or arched by tags, interior, defaults.
  function installReadyFrom(config, flags, book) {
    return prehangPrice(defaultInstallRawFor(config, flags, 'interior', 'standard_hinged', book), book);
  }

  const WAX_LABELS = { clear: 'Clear', clear_tint: 'Clear Tint', stripped_pine: 'Stripped Pine', rugger_brown: 'Rugger Brown' };
  const GLASS_LABELS = {
    remove: 'Remove the glass, leave the openings open',
    clear_tempered: 'Replace with clear tempered glass',
    diamond_pattern: 'Replace with diamond pattern glass',
    english_pattern: 'Replace with English pattern glass',
  };
  const GLASS_SHORT = { remove: 'Remove the glass', clear_tempered: 'Clear tempered', diamond_pattern: 'Diamond pattern', english_pattern: 'English pattern' };
  const GLASS_REPLACEMENTS = ['clear_tempered', 'diamond_pattern', 'english_pattern'];

  /* ── one customer name per choice (Release B, Seth 2026-10-12: "make the form very very organized") ──
     The name a shopper reads for each answer, the same on its tile, in the running total, on "Review your door" and,
     for restoration, on the /cart row. Display only: the _raw values, the line properties Eric receives
     (mad-wizard-cart.js buildProperties) and the serviceLines labels sent in estimate_lines are unchanged. The
     restoration names keep each tier's work (tier2_light and tier2_heavy include the crack repair, see
     mad-price-book.json restoration_tiers). `lines` names the running total's and the review's price lines by
     serviceLines key. */
  const CHOICE_NAMES = {
    int_ext: { interior: 'Interior', exterior: 'Exterior' },
    opening_type: { rough: 'Rough opening', finished: 'Finished opening', none: 'Not measured yet' },
    fit_readout: { close: 'Close match', resize: 'Needs resizing', talk: 'Needs a closer look', unmeasured: 'Not measured yet' },
    resize: { yes: 'Yes, resize it', no: 'No, ship it as listed' },
    application: { standard_hinged: 'Hinged', sliding_barn: 'Sliding barn', pocket_door: 'Pocket door', bifold: 'Bi-fold', decorative: 'Decorative' },
    pair_operation: { both_active: 'Both doors open', active_fixed: 'One opens, one stays closed' },
    jamb_needed: { no: 'No frame', yes: 'Install Ready (pre-hung)' },
    wall_depth: { '4_9_16': '4 9/16 in', '6_9_16': '6 9/16 in', other: 'Other' },
    hinge_color: { brushed_nickel: 'Brushed Nickel', gold: 'Brushed Gold', matte_black: 'Matte Black', antique_bronze: 'Antique Bronze', other: 'Other' },
    latch_type: { magnetic: 'Magnetic catch', ball: 'Ball catch', no_latch: 'No latch' },
    jamb_grade: { prime_pine: 'Primed pine', stain_cypress: 'Stain-grade cypress' },
    restore_tier: { none: 'As found', tier1: 'Patch and square', tier2_light: 'Patch and square, light sanding', tier2_heavy: 'Patch and square, heavy sanding', full: 'Full restoration' },
    wax_finish: { none: 'No wax', clear: 'Clear', clear_tint: 'Clear Tint', stripped_pine: 'Stripped Pine', rugger_brown: 'Rugger Brown' },
    glass_work: { none: 'Keep original glass', remove: 'Remove the glass', clear_tempered: 'Clear tempered glass', diamond_pattern: 'Diamond pattern glass', english_pattern: 'English pattern glass' },
    lines: { door: 'Door', resize: 'Resize to your opening', restoration: 'Restoration', wax: 'Wax finish', glass: 'Glass', join_pair: 'Join the pair into one door', install_ready: 'Install Ready (pre-hung)' },
  };

  /* ── launch gate: what the live Zap can bill ─────────────────────── */

  // book.zap_supports.<feature> === true only once the Zap that bills it is pasted (review of
  // 2026-09-25). Missing or false: the site behaves the way the deployed Zap bills.
  function zapSupports(book, feature) {
    const z = (book && book.zap_supports) || {};
    return z[feature] === true;
  }

  /* ── glass by pane size (D13) ─────────────────────────────────── */

  // The panes the customer typed, from the strings stored in _raw so the site and the
  // Zap read the same numbers. null when not sized ('not_sure', blank, out of range).
  // FOR APPROVAL: the limits (60 panes, 120 inches) and whole-dollar rounding below.
  function glassPanes(raw, book) {
    const g = (book && book.glass) || {};
    const maxN = g.max_panes || 60, maxIn = g.max_pane_inches || 120;
    if (!raw || raw.glass_panes === undefined || raw.glass_panes === 'not_sure') return null;
    const n = /^\d+$/.test(String(raw.glass_panes)) ? parseInt(raw.glass_panes, 10) : NaN;
    const w = parseInches(raw.glass_pane_width), h = parseInches(raw.glass_pane_height);
    if (!(n >= 1 && n <= maxN && w > 0 && h > 0 && w <= maxIn && h <= maxIn)) return null;
    return { count: n, width: w, height: h, sqft: round2(n * w * h / 144) };
  }

  // Seth, 2026-09-25 (answer 8): Eric prices replacement glass (clear tempered, diamond pattern, English
  // pattern) by hand on each quote, and Zap version 2 never bills it. So while zap_supports.glass_by_pane
  // is false (it stays false) every replacement type is 0 here and shows "priced on your quote"; removing
  // the glass keeps its flat glass_remove price. Only with the switch on (dormant): a raw without
  // glass_panes keeps the flat glass_<type> price, and sized panes are round(panes x width x height x
  // rate / 144) in whole dollars, the expression the reference Zap copy uses.
  function glassPrice(raw, book) {
    const P = (book && book.prices) || {};
    if (!raw || !raw.glass_work || raw.glass_work === 'none') return 0;
    if (raw.glass_work === 'remove') return Number(P.glass_remove) || 0;
    if (!zapSupports(book, 'glass_by_pane')) return 0; // priced by hand on the quote (answer 8)
    if (raw.glass_panes === undefined) return Number(P['glass_' + raw.glass_work]) || 0;
    const panes = glassPanes(raw, book);
    if (!panes) return 0; // priced on the quote
    return Math.round(panes.count * panes.width * panes.height * Number(P.glass_per_sqft || 0) / 144);
  }

  // Replacement glass the site does not price (every replacement type while the per-pane switch is off):
  // shown as "priced on your quote" (Seth, 2026-09-25, answer 8: Eric prices glass by hand on each quote),
  // never as $0.
  function glassOnCall(raw, book) {
    return !!raw && GLASS_REPLACEMENTS.indexOf(raw.glass_work) !== -1 && glassPrice(raw, book) === 0;
  }

  // Plain text for the email and the draft (the deployed Zap prints _raw.glass_notes as "Glass notes").
  function glassNote(raw, book) {
    if (!raw || !raw.glass_work || raw.glass_work === 'none' || raw.glass_work === 'remove') return '';
    const kind = GLASS_SHORT[raw.glass_work] || raw.glass_work;
    if (!zapSupports(book, 'glass_by_pane') || raw.glass_panes === undefined) {
      return glassOnCall(raw, book) ? kind + ' glass, not priced on the site: price it by hand on the quote' : '';
    }
    // switch on only (dormant): the pane details for Eric, never a rate (answer 8)
    const panes = glassPanes(raw, book);
    if (!panes) return kind + ' glass, pane sizes not given: price it by hand on the quote';
    return kind + ' glass, ' + panes.count + (panes.count === 1 ? ' pane ' : ' panes ') + raw.glass_pane_width + ' x ' + raw.glass_pane_height +
      ' in (' + panes.sqft + ' sq ft), ' + money(glassPrice(raw, book)) + ' on the site estimate';
  }

  /* ── freight range (D8) and trade discount (D6) ────────────────── */

  // D8 (approved): "Freight is typically about $350 to $650 for one or two doors, exact for your ZIP at
  // checkout or on your estimate." The same sentence on every template (QA: freight wording identical),
  // whatever the door count. short: the few words under an estimate bar (FOR APPROVAL: the shortened
  // form). context 'collection': no number at all, because collection pages carry no prices (8/21 rule),
  // "plus freight, quoted for your ZIP" (FOR APPROVAL). doorCount is kept for callers; it no longer
  // changes the wording.
  function freightText(doorCount, book, short, context) {
    const f = (book && book.freight) || {};
    if (context === 'collection' || !f.typical_low || !f.typical_high) return short ? 'plus freight, quoted for your ZIP' : 'Freight is quoted for your ZIP.';
    const range = 'about ' + money(f.typical_low) + ' to ' + money(f.typical_high);
    if (short) return 'plus freight, typically ' + range + ' for one or two doors';
    return 'Freight is typically ' + range + ' for one or two doors, exact for your ZIP at checkout or on your estimate.';
  }

  // Seth 2026-10-13: doors with services ship by Curbside LTL Freight (the Zap's shipping line for shop work), so the
  // services path names only that; and while the site hides prices (a test) it says no freight number at all.
  // o: { services, prices, short, context, doorCount }. Without services and with prices shown: freightText as before.
  function deliveryText(o, book) {
    o = o || {};
    const f = (book && book.freight) || {};
    const range = o.prices && o.context !== 'collection' && f.typical_low && f.typical_high ? 'about ' + money(f.typical_low) + ' to ' + money(f.typical_high) : '';
    if (o.services) {
      if (o.short) return 'plus Curbside LTL Freight' + (range ? ', typically ' + range : '');
      return 'Ships by Curbside LTL Freight' + (range ? ', typically ' + range + ' for one or two doors, exact on your quote.' : ', quoted for your address.');
    }
    if (o.prices) return freightText(o.doorCount || 1, book, !!o.short, o.context);
    return o.short ? 'plus freight, quoted for your address' : 'Freight is quoted for your address.';
  }

  // 10% of the door prices only; services and freight are never discounted. Callers show it only
  // for a known trade account and only while zap_supports.trade_discount is true (the Zap applies it).
  function tradeDiscount(doorsTotal, book) {
    const rate = Number(((book && book.trade) || {}).door_rate || 0);
    return round2((Number(doorsTotal) || 0) * rate);
  }

  // D3b wording.
  function toleranceText(book) {
    const f = (book && book.fit) || {};
    const listed = f.listed_tolerance ? formatInches(f.listed_tolerance) : '1/2';
    const serviced = f.serviced_tolerance ? formatInches(f.serviced_tolerance) : '1/8';
    return 'Listed sizes are accurate to about ' + listed + ' inch. Doors we resize or frame come out within about ' + serviced + ' inch.';
  }

  // D3a wording, from the book so the number is typed once.
  function roughOpeningText(book) {
    const c = ((book && book.fit) || {}).rough_clearance || {};
    const w = formatInches(c.width || 3), h = formatInches(c.height || 3);
    return w === h
      ? 'A rough opening is about ' + w + ' inches wider and taller than the door.'
      : 'A rough opening is about ' + w + ' inches wider and ' + h + ' inches taller than the door.';
  }

  // Service lines only (no door, no freight). Mirrors zapier_code_step.js sections 2 to 7.
  function serviceLines(raw, book) {
    const P = (book && book.prices) || {};
    const lines = [];
    if (raw.resize && raw.resize !== 'no') {
      const amt = Number(P['resize_' + raw.resize]);
      lines.push({ key: 'resize', label: 'Resize to your opening', amount: isNaN(amt) ? 0 : amt });
    }
    const restoreOn = RESTORE_KEYS.filter((k) => raw[k] === '1');
    if (restoreOn.length) {
      const sum = round2(restoreOn.reduce((s, k) => s + (Number(P[k]) || 0), 0));
      const tier = tierFromRaw(raw, book);
      const tiers = (book && book.restoration_tiers) || {};
      const label = tiers[tier] && tiers[tier].label ? 'Restoration, ' + tiers[tier].label.split(':')[0] : 'Restoration';
      lines.push({ key: 'restoration', label: label, amount: sum, items: restoreOn });
    }
    if (raw.wax_finish && raw.wax_finish !== 'none' && raw.wax_finish !== '') {
      lines.push({ key: 'wax', label: 'Wax finish, ' + (WAX_LABELS[raw.wax_finish] || raw.wax_finish), amount: Number(P.wax_finish) || 0 });
    }
    if (raw.glass_work && raw.glass_work !== 'none') {
      const byPane = raw.glass_work !== 'remove' && raw.glass_panes !== undefined && zapSupports(book, 'glass_by_pane');
      const panes = byPane ? glassPanes(raw, book) : null;
      const gl = { key: 'glass', label: 'Glass, ' + (GLASS_LABELS[raw.glass_work] || raw.glass_work), amount: glassPrice(raw, book) };
      if (byPane && panes) gl.label += ' (' + panes.count + (panes.count === 1 ? ' pane, ' : ' panes, ') + formatInches(panes.width) + ' x ' + formatInches(panes.height) + ' in)';
      /* "priced on your quote": Seth, 2026-09-25 (answer 8), for every replacement type the site does not price. */
      if (glassOnCall(raw, book)) { gl.pending = true; gl.label += ', priced on your quote'; }
      lines.push(gl);
    }
    if (raw.join_pair === 'yes') {
      lines.push({ key: 'join_pair', label: 'Join the pair into one door', amount: Number(P.join_pair_as_single) || 0 });
    }
    if (raw.jamb_needed === 'yes') {
      lines.push({ key: 'install_ready', label: 'Install Ready (pre-hung in a jamb)', amount: prehangPrice(raw, book) });
    }
    return lines;
  }

  function zapLineSum(raw, book) {
    return round2(serviceLines(raw, book).reduce((s, l) => s + l.amount, 0));
  }

  // ctx: { doorPrice (dollars), config, flags, kind }
  function estimate(raw, ctx, book) {
    const c = ctx || {};
    const doorPrice = round2(Number(c.doorPrice) || 0);
    const lines = [{ key: 'door', label: 'Door', amount: doorPrice }].concat(serviceLines(raw || {}, book));
    const total = round2(lines.reduce((s, l) => s + l.amount, 0));
    const out = { lines: lines, services: round2(total - doorPrice), total: total, doorPrice: doorPrice };
    // "Not sure, price both": show what Install Ready would add with the defaults.
    if (raw && raw.jamb_needed === 'not_sure') {
      const alt = Object.assign({}, raw, defaultInstallRawFor(c.config, c.flags, raw.int_ext, raw.application, book));
      out.installReadyAdd = prehangPrice(alt, book);
      out.totalWithInstallReady = round2(total + out.installReadyAdd);
    }
    return out;
  }

  /* ── older saved entries (Codex re-check of 0a2a0da, R04 and R10) ── */

  // The door's short display name: snippets/mad-card-name.liquid (the ONE naming rule) in JavaScript, with the three
  // facts it takes from snippets/mad-door-facts.liquid (field 0 kind, field 1 configuration, field 7 glass). Only for an
  // entry saved before the page put the name in the product object (the estimate list keeps it for later visits);
  // tests/unit/recheck-fixes.test.mjs renders the Liquid for real catalog titles and requires the same name.
  function factsKind(p) {
    const t = normalizeTags(p.tags);
    if (t.includes('newly built') || t.includes('handcrafted')) return 'handcrafted';
    const type = String(p.productType || p.product_type || p.type || '').trim().toLowerCase();
    return type === 'handcrafted doors' || type === 'handcrafted door' || type === 'handcrafted' ? 'handcrafted' : 'antique';
  }
  function factsGlass(p) {
    const t = normalizeTags(p.tags), title = String(p.title || '').toLowerCase();
    if (t.includes('glass') || t.includes('obscured glass')) return title.indexOf('solid') === -1 ? 'yes' : '';
    if (t.includes('solid')) return title.indexOf('glass') === -1 && title.indexOf('lite') === -1 ? 'no' : '';
    return '';
  }
  const NAME_NUMBERS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen',
    'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];
  const WS = /^[ \t\n\v\f\r\0]+|[ \t\n\v\f\r\0]+$/g; // Liquid's strip (Ruby): ASCII whitespace only
  function shortName(product) {
    const p = product || {}, config = doorConfig(p.tags) || '', kind = factsKind(p), glass = factsGlass(p);
    const title = String(p.title || '').replace(WS, '');
    if (title.charAt(0) === '(' && title.indexOf(')') !== -1) {
      let out = title.slice(1, 201).split(')')[0].replace(WS, '');
      if (config === 'single') out += ', single door';
      else if (config === 'pair') out += ', pair';
      else if (config === 'quad') out += ', bi-fold set';
      else if (config === 'triple') out += ', tri-fold set';
      else if (title.toLowerCase().indexOf('mirror') !== -1) out += ', mirror';
      return out || title;
    }
    if (!config) return title;
    const norm = title.toLowerCase().split('×').join('x').split('"').join('').split('”').join('').split('″').join('')
      .split('-').join(' ').split(',').join(' ').split('(').join(' ').split(')').join(' ').split('/').join(' ');
    // the words before the size (36x96, 36x, 40x25x92.25, or a lone x after a number: 36 x 96)
    let wordsText = '', pending = '', stop = false;
    for (const tok of norm.split(/[ \t\n\v\f\r]+/)) {
      if (stop) break;
      const t = tok.replace(WS, '');
      if (t === '') continue;
      const c = t.charAt(0);
      const pNum = pending !== '' && pending.replace(/[0-9.]/g, '') === '';
      if (c >= '0' && c <= '9' && t.indexOf('x') !== -1) stop = true;
      else if (c === 'x' && pNum) { stop = true; pending = ''; }
      if (pending !== '') wordsText += pending + '|';
      pending = stop ? '' : t;
    }
    // no size: the last word is the stock code when it is 1 to 3 letters and then digits
    if (pending !== '') {
      const digits = pending.replace(/[a-z]/g, ''), letters = pending.length - digits.length;
      const isCode = digits.replace(/[0-9]/g, '') === '' && digits.length > 0 && letters >= 1 && letters <= 3 && pending.slice(letters, letters + 10) === digits;
      if (!isCode) wordsText += pending + '|';
    }
    const list = wordsText.split('|');
    while (list.length && list[list.length - 1] === '') list.pop();
    const bag = '|' + wordsText, has = (w) => bag.indexOf('|' + w + '|') !== -1;
    let count = '', prev = '';
    for (const w of list) {
      if (count === '' && prev !== '' && prev.replace(/[0-9]/g, '') === '') {
        const n = parseInt(prev, 10), word = n <= 20 ? NAME_NUMBERS[n] : prev;
        if (w === 'panel' || w === 'panels' || w === 'paneled') count = word + '-panel';
        else if (w === 'lite' || w === 'lites' || w === 'light' || w === 'lights') count = word + '-light';
      }
      prev = w;
    }
    let style = '';
    if (has('arched') || has('arch')) style = 'arched';
    else if (has('carved')) style = 'carved';
    else if (has('architectural')) style = 'architectural';
    else if (has('fluted')) style = 'fluted';
    else if (has('louvered')) style = 'louvered';
    else if (has('coffered')) style = 'coffered';
    else if (count === '' && (has('paneled') || has('panel'))) style = 'paneled';
    let material = '';
    const lightCount = count.length >= 6 ? count.slice(-6) : '';
    if (has('plank')) material = 'plank';
    else if (has('solid')) { if (count === '' && style === '') material = 'solid'; }
    else if (lightCount !== '-light' && (has('glass') || has('lite') || has('lites'))) material = 'glass';
    const iron = has('iron');
    if (count === '' && style === '' && material === '' && !iron) material = glass === 'yes' ? 'glass' : glass === 'no' ? 'solid' : '';
    const use = has('pantry') ? 'pantry' : '';
    let unit = config === 'pair' ? 'pair' : config === 'quad' ? 'bi-fold set' : config === 'triple' ? 'tri-fold set' : 'door';
    if (has('shutters') || has('shutter')) unit = 'shutters';
    let out = [count, style, kind === 'handcrafted' ? 'handcrafted' : 'antique', material, use, unit].join(' ');
    let withWords = iron ? 'ironwork' : '';
    const part = has('transom') ? 'transom' : has('frame') || has('framed') ? 'frame' : '';
    if (part) withWords = withWords ? withWords + ' and ' + part : part;
    if (withWords) out += ' with ' + withWords;
    out = out.replace(WS, '').split('   ').join(' ').split('  ').join(' ').split('  ').join(' ');
    return out ? out.charAt(0).toUpperCase() + out.slice(1).toLowerCase() : title;
  }

  // An estimate list entry or a customized cart line saved before the trusted-size fix (R04) may carry advice computed
  // from a size the page no longer trusts: a fit_readout of "close" or "resize", a resize with its target and $425
  // charge, a slab_size taken from the title. normalizeSaved recomputes that advice as the wizard's buildRaw does now:
  // from the trusted size (the object's size, else the page's own hold-backs); with none, the opening goes to review
  // (fit_readout talk, "Size to be confirmed"), with no resize, no target and no slab. Only advice the site computed
  // changes: every choice the customer made stays. A resize is the site's advice only on a save that carries the site's
  // fit_readout (this site writes resize yes, with its own target, only when the readout says resize); the live theme's
  // form asked "Resize?" and took the target the customer typed, with no fit_readout, so that resize and its size stay,
  // as does any older trim, add or both. A save that carries no computed advice, or one made since the fix, comes back
  // unchanged (changed: false). When it changes, estimate_total and price_book_version follow, so the saved line agrees
  // with what the page shows.
  function savedOpening(raw) {
    if (raw.not_measured === '1' || raw.opening_type === 'none') return null;
    let w = parseInches(raw.opening_width), h = parseInches(raw.opening_height);
    if (!(w && h)) {
      const m = String(raw.rough_opening || raw.finished_opening || '').match(/^(.+?)\s*x\s*(.+)$/i);
      if (m) { w = parseInches(m[1]); h = parseInches(m[2]); }
    }
    return w && h ? { width: w, height: h } : null;
  }
  function normalizeSaved(raw, product, book) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { raw: raw, changed: false };
    const siteFit = 'fit_readout' in raw;
    if (!siteFit && !('slab_size' in raw)) return { raw: raw, changed: false };
    const p = product || {}, slab = trustedSlab(p);
    const type = raw.opening_type === 'finished' || (!raw.opening_type && raw.finished_opening) ? 'finished' : 'rough';
    const fit = fitReadout({ slab: slab, opening: savedOpening(raw), openingType: type, flags: doorFlags(p.tags), book: book });
    const out = Object.assign({}, raw);
    if ('slab_size' in out) { if (slab) out.slab_size = formatInches(slab.width) + 'x' + formatInches(slab.height); else delete out.slab_size; }
    if (siteFit) out.fit_readout = fit.state;
    if (siteFit && out.resize === 'yes') {
      if (fit.state === 'resize' && fit.suggestedTarget) { out.target_width = formatInches(fit.suggestedTarget.width); out.target_height = formatInches(fit.suggestedTarget.height); }
      else { out.resize = 'no'; delete out.target_width; delete out.target_height; }
    }
    const keys = Object.keys(raw), outKeys = Object.keys(out);
    const changed = keys.length !== outKeys.length || outKeys.some((k) => out[k] !== raw[k]);
    if (!changed) return { raw: raw, changed: false };
    if ('estimate_total' in out) {
      const ctx = { doorPrice: (Number(p.price) || 0) / 100, config: out.door_config || doorConfig(p.tags), flags: doorFlags(p.tags), kind: out.door_kind || detectDoorKind(p) };
      out.estimate_total = estimate(out, ctx, book).total.toFixed(2);
      if ('price_book_version' in out && book && book.version) out.price_book_version = String(book.version);
    }
    return { raw: out, changed: true };
  }
  // The same for a saved line's display properties (the section 8 names mad-wizard-cart.js buildProperties writes): Fit,
  // Resize and its targets, Estimate before freight and _raw follow the normalized choices, in their places; every other
  // property stays as saved. Returns { raw, properties, changed }.
  function normalizeSavedLine(raw, properties, product, book) {
    const n = normalizeSaved(raw, product, book);
    if (!n.changed) return { raw: raw, properties: properties, changed: false };
    const r = n.raw, src = properties || {}, out = {};
    const fitText = r.fit_readout && FIT_TEXT[r.fit_readout] ? FIT_TEXT[r.fit_readout] : '';
    const targets = () => { if (r.resize === 'yes' && r.target_width) out['Target Width'] = r.target_width + ' in'; if (r.resize === 'yes' && r.target_height) out['Target Height'] = r.target_height + ' in'; };
    let targetsDone = false;
    Object.keys(src).forEach((k) => {
      if (k === 'Fit') { if (fitText) out.Fit = fitText; return; }
      if (k === 'Resize') { out.Resize = r.resize === 'yes' ? 'Yes' : r.resize && r.resize !== 'no' ? src[k] : 'None'; targets(); targetsDone = true; return; }
      if (k === 'Target Width' || k === 'Target Height') { if (!targetsDone) { targets(); targetsDone = true; } return; }
      if (k === 'Estimate before freight') { out[k] = r.estimate_total ? money(Number(r.estimate_total)) : src[k]; return; }
      if (k === '_raw') { out._raw = JSON.stringify(r); return; }
      out[k] = src[k];
    });
    if (!('_raw' in out)) out._raw = JSON.stringify(r);
    return { raw: r, properties: out, changed: true };
  }

  return {
    parseInches, formatInches, money, round2, roundTo,
    normalizeTags, doorConfig, isDoor, CONFIG_LABELS, doorFlags, detectDoorKind, kindTitle, KIND_SEPARATORS,
    parseSlabSize, formatSlab, doorCode, doorLabel, SIZE_RE, trustedSlab, SIZE_TO_CONFIRM, SIZE_TO_CONFIRM_TEXT,
    RESTORE_KEYS, tierKeys, expandTier, tierPrice, tierFromRaw,
    deriveJambType, fitReadout, FIT_TEXT,
    prehangPrice, defaultInstallRawFor, installReadyFrom, serviceLines, zapLineSum, estimate,
    WAX_LABELS, GLASS_LABELS, GLASS_SHORT, GLASS_REPLACEMENTS, CHOICE_NAMES, glassPanes, glassPrice, glassOnCall, glassNote,
    freightText, deliveryText, tradeDiscount, toleranceText, roughOpeningText, zapSupports,
    shortName, normalizeSaved, normalizeSavedLine,
  };
});
